import { useCallback, useContext, useEffect, useState } from 'react';

import {
	Button,
	Col,
	Divider,
	Drawer,
	Flex,
	Form,
	Select,
	message,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import TextArea from 'antd/es/input/TextArea';
import { UploadFile } from 'antd/lib';

import { useCreateCopilotBatch } from '@/api/batch_process';
import FileUpload from '@/components/common/FileUploader';
import validateBatchCreatorsUrls from '@/lib/helpers/validateBatchCreatorsUrls';
import { PageContext } from '@/providers/PageContextProvider';

export default function SendBatchDrawer() {
	const { drawer, closeDrawer } = useContext(PageContext);
	const [content, setContent] = useState<string>('');
	const [uploadedFile, setUploadedFile] = useState<UploadFile | null>(null);

	const [form] = useForm();

	const { mutate, isLoading } = useCreateCopilotBatch();

	const onCancelClick = () => {
		form.resetFields();
		setUploadedFile(null);
		setContent('');
		closeDrawer();
	};

	const onSaveClick = useCallback(async () => {
		if (uploadedFile && content) {
			const urls = content
				.split('\n')
				.map((item) => item.trim())
				.filter((item) => item !== '' && item !== '\n');

			if (urls.length === 0) {
				message.error(
					'The uploaded file is empty or contains only empty lines.',
				);
				return;
			} else if (urls.length > 100) {
				message.error('Maximum 100 URLs are allowed / check empty lines');
				return;
			}

			validateBatchCreatorsUrls(urls)
				.then(() => {
					mutate(
						{
							url: urls?.map((item: string) => {
								const preUrl = item?.includes('&')
									? item?.split('&')?.[0]
									: item;

								if (preUrl?.includes('youtube.com/shorts/')) {
									return preUrl?.replace(
										'youtube.com/shorts/',
										'youtube.com/watch?v=',
									);
								}

								return preUrl.trim();
							}),
						},
						{
							onSuccess: () => {
								form.resetFields();
								setUploadedFile(null);
								setContent('');
								message.success({ content: 'Sent' });
								closeDrawer();
							},
							onError: (error: any) => {
								message.error({
									type: 'error',
									content:
										error?.response?.data?.message ?? error?.message ?? 'Error',
								});
							},
						},
					);
				})
				.catch((err) => {
					message.error(err.message);
				});
		} else {
			try {
				await form.validateFields();

				const { urls } = form.getFieldsValue();

				mutate(
					{
						url: urls?.split('\n').map((item: string) => {
							const preUrl = item?.includes('&') ? item?.split('&')?.[0] : item;

							if (preUrl?.includes('youtube.com/shorts/')) {
								return preUrl?.replace(
									'youtube.com/shorts/',
									'youtube.com/watch?v=',
								);
							}

							return preUrl.trim();
						}),
					},
					{
						onSuccess: () => {
							form.resetFields();
							message.success({ content: 'Sent' });
							closeDrawer();
						},
						onError: (error: any) => {
							message.error({
								type: 'error',
								content:
									error?.response?.data?.message ?? error?.message ?? 'Error',
							});
						},
					},
				);
			} catch (error) {
				console.log(error);
			}
		}
	}, [content]);

	return (
		<Drawer
			open={drawer.isOpen && drawer.type === 'add-new-copilot-batch'}
			width={'650px'}
			onClose={onCancelClick}
			title="Copilot - new batch"
		>
			<Col span={24}>
				<Flex
					justify="end"
					align="center"
					gap={20}
					style={{ marginBottom: '1.25rem' }}
				>
					<Button onClick={onCancelClick}>Cancel</Button>
					<Button type="primary" onClick={onSaveClick} loading={isLoading}>
						Save
					</Button>
				</Flex>
			</Col>
			<Form form={form} layout="vertical">
				<Form.Item
					label={
						<Flex vertical>
							<span>Video URLs to analyse</span>
							<small>(one URL per line, max URLs: 100)</small>
						</Flex>
					}
					name="urls"
					rules={[
						{
							required: true,
							message: 'URLs are required',
						},
						{
							whitespace: true,
							message: 'At least one URL is required',
						},
						{
							validator: (_, value) => {
								if (!value || value.split('\n').length === 0) {
									return Promise.reject(
										new Error('At least one URL is required'),
									);
								}

								const urls: string[] = value.split('\n');

								if (urls.length > 100) {
									return Promise.reject(
										new Error(
											'Maximum 100 URLs are allowed / check empty lines',
										),
									);
								}

								validateBatchCreatorsUrls(urls);

								return Promise.resolve();
							},
						},
					]}
				>
					<TextArea
						rows={12}
						placeholder={`https://www.youtube.com/watch?v=jNQXAC9IVRw\nhttps://www.instagram.com/p/C-GFnTrJSNi/\nhttps://www.tiktok.com/@look556666/video/7184420791466118446`}
					/>
				</Form.Item>

				<Divider plain style={{ color: 'gray' }}>
					OR
				</Divider>

				<FileUpload
					uploadedFile={uploadedFile}
					setUploadedFile={setUploadedFile}
					setContent={setContent}
				/>
			</Form>
		</Drawer>
	);
}
